import type {AuditProcedure, FraudRiskFactor, FraudTeamDiscussion, ManagementOverrideArea, Risk} from "../types";

// --- Fraud Risk Factors (Fraud Triangle) ---

export const FRAUD_RISK_FACTORS_INCENTIVE: FraudRiskFactor[] = [
  {
    id: "frf-i-01",
    description:
      "EBITDA-linked compensation for senior management creates direct financial incentive to manipulate earnings",
    riskLevel: "High",
    sourceTags: ["Compensation review", "Board minutes"],
  },
  {
    id: "frf-i-02",
    description: "Debt covenant pressure — 3.5x leverage ratio approaching covenant threshold of 4.0x",
    riskLevel: "High",
    sourceTags: ["Loan agreement", "Q3 financials"],
  },
  {
    id: "frf-i-03",
    description: "H2 revenue uncertainty due to delayed contract renewals in manufacturing division",
    riskLevel: "Medium",
    sourceTags: ["Sales pipeline review", "Management inquiry"],
  },
];

export const FRAUD_RISK_FACTORS_OPPORTUNITY: FraudRiskFactor[] = [
  {
    id: "frf-o-01",
    description:
      "Complex revenue arrangements with manual adjustments for multi-element contracts allow subjective allocation",
    riskLevel: "High",
    sourceTags: ["Revenue process walkthrough", "Contract samples"],
  },
  {
    id: "frf-o-02",
    description: "Limited segregation of duties for journal entry posting — 3 users can both prepare and post entries",
    riskLevel: "High",
    sourceTags: ["IT access review", "JE process walkthrough"],
  },
  {
    id: "frf-o-03",
    description: "Inventory held at 4 external warehouses with limited independent verification procedures",
    riskLevel: "Medium",
    sourceTags: ["Inventory process review", "Warehouse agreements"],
  },
  {
    id: "frf-o-04",
    description:
      "Related party transactions with board-member-controlled supplier (Westfield Components Ltd) totalling £2.3M",
    riskLevel: "High",
    sourceTags: ["Related party disclosure", "Board declarations"],
  },
];

export const FRAUD_RISK_FACTORS_RATIONALIZATION: FraudRiskFactor[] = [
  {
    id: "frf-r-01",
    description:
      '"Meet the numbers" culture — management communications repeatedly emphasise hitting analyst consensus estimates',
    riskLevel: "High",
    sourceTags: ["Investor calls", "Internal comms review"],
  },
  {
    id: "frf-r-02",
    description: "Previous auditor noted tone-at-top deficiency in prior year management letter (unresolved)",
    riskLevel: "Medium",
    sourceTags: ["Prior year audit file", "Management letter"],
  },
  {
    id: "frf-r-03",
    description: "Code of conduct training last refreshed 28 months ago — no attestation process in place",
    riskLevel: "Medium",
    sourceTags: ["HR records", "Compliance review"],
  },
];

// --- Team Discussion (ISA 240.15) ---

export const FRAUD_TEAM_DISCUSSION: FraudTeamDiscussion = {
  date: "15 Jan 2026",
  participants: [
    "Henry Irish (Engagement Partner)",
    "Sarah Chen (Audit Manager)",
    "James Wilson (Senior)",
    "Priya Patel (Assistant)",
  ],
  location: "Platformed HQ — Room 4B (recorded via Teams)",
  videoMeta: {duration: "47:23", uploadedAt: "15 Jan 2026"},
  transcript: [
    {
      speaker: "Henry Irish",
      timestamp: "00:00:15",
      text: "Let's open the ISA 240 discussion. The purpose today is to discuss how and where Johnstone's financial statements may be susceptible to material misstatement due to fraud. I want everyone's input — professional skepticism means we set aside assumptions about management's integrity for this session.",
    },
    {
      speaker: "Sarah Chen",
      timestamp: "00:02:15",
      text: "Starting with financial statement susceptibility — the areas I see as most exposed are revenue recognition on the multi-element contracts and the manual journal entry process. Revenue has the presumed fraud risk, but the complexity of their bundled arrangements makes it particularly acute here. On JEs, the lack of segregation we identified in the IT walkthrough is concerning.",
    },
    {
      speaker: "James Wilson",
      timestamp: "00:05:42",
      text: "I'd add inventory to that list. They've got stock at four warehouses and the count procedures rely heavily on the warehouse operators' own records. With the EBITDA pressure, overstating inventory is a straightforward way to improve margins. The Westfield related party is also a vector — £2.3M with a board-controlled supplier is material.",
    },
    {
      speaker: "Priya Patel",
      timestamp: "00:08:30",
      text: "From the data analytics I ran on the JE population, there's a cluster of round-number entries above £50K posted on the last two days of each quarter. Around 15 entries in H1 that fit that pattern. Also noticed that two of the three users with dual prepare/post access are in the Finance Director's team.",
    },
    {
      speaker: "Henry Irish",
      timestamp: "00:12:10",
      text: "Good. Let's talk about the presumed risk on revenue recognition. Given the contract complexity Sarah mentioned, I don't think we can rebut the presumption. The multi-element arrangements involve significant judgment on SSP allocation — management has both the incentive and the opportunity to manipulate timing and allocation.",
    },
    {
      speaker: "Sarah Chen",
      timestamp: "00:18:45",
      text: "Agreed. And on management override — this is non-rebuttable per ISA 240. The JE findings Priya mentioned reinforce the need for a thorough journal entry testing programme. I'd recommend we do full-population analytics rather than sampling.",
    },
    {
      speaker: "James Wilson",
      timestamp: "00:25:00",
      text: "For the brainstorming element — other scenarios to consider: capitalisation of expenses to boost EBITDA given the covenant pressure, and manipulation of the inventory obsolescence provision. Both are estimate-based and harder to detect through standard testing.",
    },
    {
      speaker: "Henry Irish",
      timestamp: "00:35:15",
      text: "To summarise — five identified fraud risks: revenue recognition fraud, management override via JEs, inventory overstatement, improper capitalisation, and related party manipulation. Professional skepticism should be particularly heightened around period-end adjustments and any entries involving the three dual-access users. Let's document the responses and move to the audit programme.",
    },
  ],
  agendaTopics: [
    {
      title: "Financial Statement Susceptibility to Fraud",
      summary:
        "The team identified **revenue recognition** (multi-element contract complexity), **journal entries** (segregation of duties gap), and **inventory** (multiple warehouse locations with limited verification) as the areas most susceptible to material misstatement due to fraud. The EBITDA-linked compensation structure and debt covenant pressure at 3.5x leverage were noted as heightening incentives across all areas.",
      citations: [
        {
          id: "fraud-tc-1",
          citationId: 1,
          name: "Team Discussion Recording",
          sourceType: "Video",
          scope: "00:02:15 – 00:08:30",
          context: "Sarah Chen and James Wilson discuss revenue, JE, and inventory susceptibility",
        },
      ],
    },
    {
      title: "Entity and Industry-Specific Fraud Risk Factors",
      summary:
        'Industry-specific factors include the manufacturing sector\'s inherent exposure to inventory manipulation and the complexity of multi-element revenue arrangements common in contract manufacturing. Entity-specific factors include the **"meet the numbers" culture** identified through investor communications, the **unresolved tone-at-top deficiency** from the prior auditor, and the **£2.3M related party relationship** with a board-member-controlled supplier (Westfield Components Ltd).',
      citations: [
        {
          id: "fraud-tc-2",
          citationId: 2,
          name: "Team Discussion Recording",
          sourceType: "Video",
          scope: "00:05:42 – 00:08:30",
          context: "James Wilson raises inventory and related party risks; Priya Patel shares JE analytics findings",
        },
      ],
    },
    {
      title: "Revenue Recognition Presumed Risk",
      summary:
        "The team concluded that the **presumed fraud risk on revenue recognition cannot be rebutted**. The combination of multi-element arrangements requiring subjective SSP allocation, management incentive through EBITDA-linked compensation, and the H2 revenue uncertainty creates conditions where manipulation of timing and allocation is both feasible and motivated. The presumed risk will be addressed through substantive testing of a risk-stratified contract sample.",
      citations: [
        {
          id: "fraud-tc-3",
          citationId: 3,
          name: "Team Discussion Recording",
          sourceType: "Video",
          scope: "00:12:10 – 00:18:45",
          context: "Henry Irish and Sarah Chen discuss non-rebuttable presumed risk on revenue recognition",
        },
      ],
    },
    {
      title: "Brainstorming — Additional Fraud Scenarios",
      summary:
        "Beyond the three primary risks, the team identified two additional scenarios: **improper expense capitalisation** to inflate EBITDA (particularly relevant given covenant pressure) and **related party transaction manipulation** through the Westfield Components supply relationship. Data analytics on the JE population revealed a pattern of **round-number entries >£50K in the final two days of each quarter** warranting investigation.",
      citations: [
        {
          id: "fraud-tc-4",
          citationId: 4,
          name: "Team Discussion Recording",
          sourceType: "Video",
          scope: "00:08:30 – 00:12:10",
          context: "Priya Patel presents JE data analytics findings on round-number entries",
        },
        {
          id: "fraud-tc-5",
          citationId: 5,
          name: "Team Discussion Recording",
          sourceType: "Video",
          scope: "00:25:00 – 00:35:15",
          context: "James Wilson identifies capitalisation and inventory provision manipulation scenarios",
        },
      ],
    },
    {
      title: "Professional Skepticism and Next Steps",
      summary:
        "The engagement partner emphasised maintaining **heightened professional skepticism** around period-end adjustments and entries involving the three users with dual prepare/post access. Five fraud risks will be documented in the planning file with specific audit responses. Full-population journal entry analytics (rather than sampling) was agreed as the approach for management override testing.",
      citations: [
        {
          id: "fraud-tc-6",
          citationId: 6,
          name: "Team Discussion Recording",
          sourceType: "Video",
          scope: "00:18:45 – 00:25:00",
          context: "Sarah Chen recommends full-population analytics over sampling for JE testing",
        },
        {
          id: "fraud-tc-7",
          citationId: 7,
          name: "Team Discussion Recording",
          sourceType: "Video",
          scope: "00:35:15 – 00:47:23",
          context: "Henry Irish summarises five fraud risks and assigns heightened skepticism areas",
        },
      ],
    },
  ],
};

// --- Identified Fraud Risks ---

export const FRAUD_RISKS: Risk[] = [
  {
    id: "fraud-risk-01",
    riskNumber: "FR-01",
    title: "Revenue recognition fraud — premature revenue on multi-element contracts",
    description:
      "Management may manipulate the timing and allocation of revenue across performance obligations in bundled manufacturing contracts, resulting in premature or overstated revenue recognition. This is a presumed fraud risk under ISA 240.",
    owner: "Sarah Chen",
    inherentScore: {
      components: {complexity: 4, subjectivity: 4, change: 3, uncertainty: 3, fraudSusceptibility: 5},
      rationale:
        "Multi-element arrangements require significant judgment on SSP allocation. Fraud susceptibility is maximum given the direct link between revenue and EBITDA-linked compensation.",
      assessedBy: "Henry Irish",
      assessedAt: new Date("2026-01-16"),
    },
    residualLevel: "significant",
    residualScore: null,
    status: "in-review",
    cotabdAssertions: [
      {cotabdId: "cotabd-rev-transactions", assertionId: "existence"},
      {cotabdId: "cotabd-rev-transactions", assertionId: "accuracy"},
      {cotabdId: "cotabd-rev-transactions", assertionId: "cutoff"},
    ],
    controls: [],
  },
  {
    id: "fraud-risk-02",
    riskNumber: "FR-02",
    title: "Management override via manual journal entries",
    description:
      "Management may use manual journal entries to manipulate financial results, particularly through period-end adjustments, round-number entries, or entries posted by users with dual prepare/post access. This is a presumed, non-rebuttable fraud risk under ISA 240.",
    owner: "Sarah Chen",
    inherentScore: {
      components: {complexity: 3, subjectivity: 3, change: 2, uncertainty: 3, fraudSusceptibility: 5},
      rationale:
        "Journal entry manipulation is the most common method of management override. The segregation of duties gap (3 dual-access users) increases susceptibility.",
      assessedBy: "Henry Irish",
      assessedAt: new Date("2026-01-16"),
    },
    residualLevel: "significant",
    residualScore: null,
    status: "in-review",
    cotabdAssertions: [
      {cotabdId: "cotabd-exp-transactions", assertionId: "existence"},
      {cotabdId: "cotabd-exp-transactions", assertionId: "accuracy"},
      {cotabdId: "cotabd-rev-transactions", assertionId: "existence"},
      {cotabdId: "cotabd-rev-transactions", assertionId: "accuracy"},
      {cotabdId: "cotabd-fr-disclosure", assertionId: "completeness"},
    ],
    controls: [],
  },
  {
    id: "fraud-risk-03",
    riskNumber: "FR-03",
    title: "Inventory overstatement via stock count manipulation",
    description:
      "Management may manipulate inventory quantities or valuations at the four external warehouse locations, resulting in overstated inventory balances and understated cost of goods sold to improve reported EBITDA.",
    owner: "James Wilson",
    inherentScore: {
      components: {complexity: 3, subjectivity: 3, change: 2, uncertainty: 4, fraudSusceptibility: 4},
      rationale:
        "Multiple warehouse locations with reliance on operators' records creates opportunity. EBITDA pressure provides direct incentive for overstatement.",
      assessedBy: "Henry Irish",
      assessedAt: new Date("2026-01-16"),
    },
    residualLevel: "significant",
    residualScore: null,
    status: "in-review",
    cotabdAssertions: [
      {cotabdId: "cotabd-exp-balance", assertionId: "existence"},
      {cotabdId: "cotabd-exp-balance", assertionId: "valuation"},
    ],
    controls: [],
  },
  {
    id: "fraud-risk-04",
    riskNumber: "FR-04",
    title: "Improper expense capitalisation to meet EBITDA targets",
    description:
      "Management may capitalise expenses that should be recognised in the income statement to inflate EBITDA, particularly given the 3.5x leverage covenant threshold approaching 4.0x.",
    owner: "James Wilson",
    inherentScore: {
      components: {complexity: 2, subjectivity: 3, change: 3, uncertainty: 2, fraudSusceptibility: 4},
      rationale:
        "Capitalisation vs. expense judgments are inherently subjective. The covenant pressure creates a specific incentive to shift expenses off the income statement.",
      assessedBy: "Sarah Chen",
      assessedAt: new Date("2026-01-17"),
    },
    residualLevel: "medium",
    residualScore: null,
    status: "in-progress",
    cotabdAssertions: [
      {cotabdId: "cotabd-exp-transactions", assertionId: "presentation"},
      {cotabdId: "cotabd-exp-transactions", assertionId: "accuracy"},
    ],
    controls: [],
  },
  {
    id: "fraud-risk-05",
    riskNumber: "FR-05",
    title: "Related party transaction manipulation",
    description:
      "Management may manipulate transactions with Westfield Components Ltd (board-member-controlled supplier, £2.3M annual volume) to misstate expenditures or shift profits between related entities.",
    owner: "Priya Patel",
    inherentScore: {
      components: {complexity: 3, subjectivity: 3, change: 2, uncertainty: 3, fraudSusceptibility: 4},
      rationale:
        "Related party transactions with a board-controlled entity create both opportunity and concealment risk. The £2.3M volume is material.",
      assessedBy: "Sarah Chen",
      assessedAt: new Date("2026-01-17"),
    },
    residualLevel: "medium",
    residualScore: null,
    status: "in-progress",
    cotabdAssertions: [
      {cotabdId: "cotabd-rev-transactions", assertionId: "completeness"},
      {cotabdId: "cotabd-rev-transactions", assertionId: "valuation"},
      {cotabdId: "cotabd-fr-disclosure", assertionId: "completeness"},
      {cotabdId: "cotabd-fr-disclosure", assertionId: "valuation"},
    ],
    controls: [],
  },
];

// --- Management Override Areas ---

export const FRAUD_MANAGEMENT_OVERRIDE_AREAS: ManagementOverrideArea[] = [
  {
    id: "mo-je",
    title: "Journal Entry Testing",
    description: `Data analytics will be applied over the **full journal entry population** for the year, targeting entries with characteristics indicative of management override:

- **Off-hours posting**: Entries created outside normal business hours (before 07:00 or after 20:00)
- **Unusual posters**: Entries by individuals who rarely post JEs or by the three users with dual prepare/post access
- **Revenue/COGS impact**: Entries with debit or credit to revenue or cost of goods sold accounts
- **Round numbers**: Entries with amounts >£50,000 that are exact round numbers
- **Period-end clustering**: Entries posted in the final 3 business days of each quarter

The full-population approach was selected over sampling because the data analytics identified a cluster of 15 round-number entries >£50K in the final two days of each quarter during H1, warranting comprehensive investigation.`,
    procedureIds: ["ap-frd-01", "ap-frd-02"],
  },
  {
    id: "mo-estimates",
    title: "Accounting Estimates Review",
    description: `A **retrospective review** of significant accounting estimates will assess whether prior-period estimates indicate management bias:

- **Inventory obsolescence provision**: Compare prior-year provision to actual write-offs during the current year. Assess whether the methodology has been consistently applied or adjusted in ways that systematically favour higher EBITDA.
- **Warranty provision**: Review the assumptions underlying the warranty accrual against actual claims experience. Test for evidence of under-provisioning.
- **Revenue contract modifications**: For multi-element contracts recognised over time, compare original estimates of standalone selling prices and completion percentages to subsequent outcomes.

For each estimate, we will assess the **range of reasonable outcomes** and evaluate whether management's point estimate consistently falls at the favourable end of the range.`,
    procedureIds: ["ap-frd-03", "ap-frd-04"],
  },
  {
    id: "mo-unusual",
    title: "Significant Unusual Transactions",
    description: `Procedures will be designed to identify and evaluate transactions that are outside the normal course of business or otherwise unusual:

- **Year-end adjustments**: All manual adjustments posted after the preliminary close but before financial statement issuance
- **Related party transactions**: Comprehensive review of all transactions with Westfield Components Ltd and any other identified related parties, including pricing benchmarking against arm's length terms
- **Asset disposals**: Any disposals of property, plant, or equipment in the final quarter, particularly where gains are recognised

Each significant unusual transaction will be evaluated for **business rationale**, **authorisation**, and **accounting treatment appropriateness**.`,
    procedureIds: ["ap-frd-05"],
  },
];

// --- Fraud Procedures ---

export const FRAUD_PROCEDURES: AuditProcedure[] = [
  {
    id: "ap-frd-01",
    procedureNumber: "AP-FRD-01",
    title: "Full-population journal entry data analytics",
    description:
      "Run data analytics over the complete journal entry population for the year, applying risk criteria (off-hours, unusual posters, revenue/COGS impact, round numbers >£50K, period-end clustering). Investigate all flagged entries.",
    methodology: "substantive-detail",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "Full population",
    assignedTo: "Priya Patel",
    status: "in-progress",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["existence", "accuracy", "completeness"],
    riskIds: ["fraud-risk-02"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-02",
    procedureNumber: "AP-FRD-02",
    title: "Test journal entries posted by dual-access users",
    description:
      "For the three users with dual prepare/post access, select all journal entries above materiality and test for appropriate business rationale, supporting documentation, and independent review evidence.",
    methodology: "substantive-detail",
    techniques: ["inspection", "inquiry"],
    timing: "year-end",
    extent: "All entries by dual-access users >£225K",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["existence", "accuracy"],
    riskIds: ["fraud-risk-02"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-03",
    procedureNumber: "AP-FRD-03",
    title: "Retrospective review of accounting estimates",
    description:
      "Perform retrospective analysis of prior-year estimates (inventory obsolescence, warranty provision, revenue contract modifications) comparing estimates to actual outcomes to identify systematic bias.",
    methodology: "substantive-analytical",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "All significant estimates",
    assignedTo: "Sarah Chen",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["valuation", "accuracy"],
    riskIds: ["fraud-risk-02"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-04",
    procedureNumber: "AP-FRD-04",
    title: "Evaluate range of reasonable outcomes for key estimates",
    description:
      "For each significant estimate, independently develop the range of reasonable outcomes and assess whether management's point estimate consistently falls at the favourable end, indicating potential bias.",
    methodology: "substantive-analytical",
    techniques: ["reperformance"],
    timing: "year-end",
    extent: "3 key estimates",
    assignedTo: "Sarah Chen",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["valuation"],
    riskIds: ["fraud-risk-02"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-05",
    procedureNumber: "AP-FRD-05",
    title: "Evaluate significant unusual transactions",
    description:
      "Identify and evaluate all significant unusual transactions including year-end adjustments, related party transactions with Westfield Components, and Q4 asset disposals. Assess business rationale and accounting treatment.",
    methodology: "substantive-detail",
    techniques: ["inspection", "inquiry"],
    timing: "year-end",
    extent: "All identified transactions",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["existence", "accuracy", "completeness", "presentation"],
    riskIds: ["fraud-risk-05"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-06",
    procedureNumber: "AP-FRD-06",
    title: "Revenue recognition fraud testing — risk-stratified contract sample",
    description:
      "Select a risk-stratified sample of multi-element contracts and independently assess revenue recognition: performance obligation identification, SSP allocation, timing of recognition, and variable consideration estimates.",
    methodology: "substantive-detail",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "Sample of 15 contracts",
    assignedTo: "Sarah Chen",
    status: "not-ready",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-transactions",
    assertionIds: ["existence", "accuracy", "cutoff"],
    riskIds: ["fraud-risk-01"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-07",
    procedureNumber: "AP-FRD-07",
    title: "Inventory existence testing at external warehouses",
    description:
      "Attend inventory counts at all four external warehouse locations. Perform independent test counts and reconcile to the perpetual inventory system. Investigate any discrepancies above trivial threshold.",
    methodology: "substantive-detail",
    techniques: ["observation", "inspection", "reperformance"],
    timing: "year-end",
    extent: "All 4 warehouses",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-balance",
    assertionIds: ["existence", "valuation"],
    riskIds: ["fraud-risk-03"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
];

// --- Fraud Summary (Audit Strategy narrative) ---

export const FRAUD_SUMMARY = `The overall fraud risk assessment for Johnstone's Ltd is **elevated**, driven primarily by the combination of EBITDA-linked compensation, debt covenant pressure approaching the 4.0x threshold, and identified control gaps in journal entry segregation of duties.

## Key Risks and Responses

**Revenue recognition fraud (FR-01)** is the highest-priority fraud risk. The multi-element contract arrangements require significant judgment on SSP allocation and performance obligation identification, and the presumed fraud risk cannot be rebutted given management's incentive structure. Our response centres on substantive testing of a risk-stratified sample of 15 contracts (AP-FRD-06), with particular focus on bundled arrangements and contracts with variable consideration.

**Management override via journal entries (FR-02)** is a non-rebuttable presumed risk that is reinforced by the entity-specific finding of three users with dual prepare/post access. We have adopted a **full-population analytics approach** (AP-FRD-01) rather than sampling, supplemented by targeted testing of all entries by dual-access users above materiality (AP-FRD-02).

**Inventory overstatement (FR-03)** is addressed through attendance at all four external warehouse locations (AP-FRD-07) with independent test counts. The reliance on warehouse operators' records means we cannot take a reduced approach here.

**Expense capitalisation (FR-04)** and **related party manipulation (FR-05)** are medium-rated risks addressed through the significant unusual transactions procedure (AP-FRD-05) and the retrospective estimates review (AP-FRD-03, AP-FRD-04).

## Management Override Assessment

The ISA 240 mandatory procedures for management override are comprehensive:
- **Journal entry testing**: Full-population data analytics with five risk criteria, plus targeted testing of dual-access user entries
- **Accounting estimates**: Retrospective review of three key estimates with independent range-of-outcomes analysis
- **Unusual transactions**: Evaluation of year-end adjustments, related party transactions, and Q4 asset disposals

## Areas of Ongoing Attention

Professional skepticism should be particularly heightened around **period-end adjustments** and **entries involving the three dual-access users**. The team should remain alert to any indicators of management pressure on the Finance team during the year-end close process. The related party relationship with Westfield Components requires ongoing monitoring for any changes in transaction volume or terms.`;
